import React, { useState, useEffect, useRef } from 'react';
import { Streamdown } from 'streamdown';
import { TYPING_SPEED as DEFAULT_TYPING_SPEED } from '../utils/textFormatting';

interface TypingTextProps {
  text: string;
  speed?: number; // Speed in milliseconds per character (default: 15 - rapid typing)
  onComplete?: () => void;
  className?: string;
  isStreaming?: boolean; // Indicates if text is being streamed
}

const TypingText: React.FC<TypingTextProps> = ({ 
  text, 
  speed = DEFAULT_TYPING_SPEED, 
  onComplete, 
  className,
  isStreaming = false
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [showCursor, setShowCursor] = useState(true); // 🚀 PERFORMANCE: State for cursor visibility (prevents flicker)
  const animationRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const targetTextRef = useRef<string>(''); // Target text to animate towards
  const completedRef = useRef<boolean>(false); // Track if animation completed
  const currentLengthRef = useRef<number>(0); // Track current displayed length (avoids stale state)
  // 🎬 REMOVED (Nov 25, 2025): Debounce timer no longer needed
  // Chat.tsx now handles all buffering via 50ms periodic interval
  
  // Store previous values to detect what changed in useEffect
  const prevTextRef = useRef(text);
  const prevSpeedRef = useRef(speed);
  const prevIsStreamingRef = useRef(isStreaming);
  const prevOnCompleteRef = useRef(onComplete);

  // 🎬 SMOOTH ANIMATION LOGIC: Let animation finish naturally (Nov 22, 2025)
  // ❌ REMOVED: Instant text dump when !isStreaming (caused animation skipping)
  // ✅ NEW: Let requestAnimationFrame loop finish animation at its own pace
  // Even when isStreaming becomes false, animation continues until displayedText === text
  useEffect(() => {
    // Update refs for next comparison
    prevTextRef.current = text;
    prevSpeedRef.current = speed;
    prevIsStreamingRef.current = isStreaming;
    prevOnCompleteRef.current = onComplete;
    
    // 🎬 FIX (Nov 25, 2025): SIMPLIFIED - No debouncing!
    // Chat.tsx now handles all buffering via 50ms periodic interval
    // TypingText just animates whatever text it receives
    // This eliminates the double-buffering issue that caused text to appear all at once
    
    // 🎬 FIX (Nov 25, 2025): Remove debounce entirely!
    // Chat.tsx already handles buffering via 50ms periodic interval
    // Double buffering (Chat.tsx + TypingText debounce) caused animation lag
    // Now: Update target immediately, let animation catch up smoothly
    targetTextRef.current = text;
    
    // Reset completion flag when new text arrives
    if (text.length > currentLengthRef.current) {
      completedRef.current = false;
      setShowCursor(true);
    }
    
    // Initialize current length ref if needed
    if (currentLengthRef.current === 0 && displayedText.length > 0) {
      currentLengthRef.current = displayedText.length;
    }

    // 🎬 Continuous animation loop (runs until displayedText === text)
    // 🚀 PERFORMANCE: Removed excessive logging for smooth animation
    const animate = () => {
      const now = Date.now();
      
      // 🎬 ADAPTIVE SPEED (Dec 4, 2025): Accelerate when backlog is large
      // This ensures animation catches up when text arrives faster than base speed
      const pendingChars = targetTextRef.current.length - currentLengthRef.current;
      let effectiveSpeed = speed;
      
      if (pendingChars > 100) {
        effectiveSpeed = speed / 4; // 4x faster when huge backlog (>100 chars)
      } else if (pendingChars > 50) {
        effectiveSpeed = speed / 3; // 3x faster when big backlog (50-100 chars)
      } else if (pendingChars > 20) {
        effectiveSpeed = speed / 2; // 2x faster when medium backlog (20-50 chars)
      }
      // Below 20 chars pending: use normal speed for visible animation
      
      // Throttle updates based on effective speed
      if (now - lastUpdateRef.current >= effectiveSpeed) {
        setDisplayedText(prev => {
          const target = targetTextRef.current;
          
          // If we've caught up to target, check if animation is complete
          if (prev.length >= target.length) {
            currentLengthRef.current = prev.length;
            
            // Call onComplete when animation reaches final text
            if (!completedRef.current && onComplete && prev === text) {
              completedRef.current = true;
              setShowCursor(false); // Hide cursor when complete
              onComplete();
            }
            return prev;
          }
          
          // Otherwise, add next character
          const nextText = target.substring(0, prev.length + 1);
          currentLengthRef.current = nextText.length; // Update ref immediately
          return nextText;
        });
        lastUpdateRef.current = now;
      }
      
      // 🚨 FIX (Nov 22, 2025): Continue animation until displayedText === text
      // Don't stop just because isStreaming is false - finish what we started!
      if (currentLengthRef.current < targetTextRef.current.length) {
        animationRef.current = requestAnimationFrame(animate);
      } else if (currentLengthRef.current < text.length) {
        // Edge case: Target caught up, but full text not displayed yet
        animationRef.current = requestAnimationFrame(animate);
      } else if (isStreaming) {
        // 🎬 FIX CURSOR FLICKER (Dec 5, 2025): Keep cursor visible while streaming!
        // Animation caught up to current target, but more text is coming
        // Keep the animation loop running so it can pick up new text
        // DON'T hide cursor here - we're waiting for more characters
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete AND no more text coming - hide cursor
        setShowCursor(false);
        
        // Force final completion check
        setDisplayedText(prev => {
          if (!completedRef.current && onComplete && prev === text) {
            completedRef.current = true;
            onComplete();
          }
          return prev; // No change to displayed text
        });
      }
    };

    // Start animation loop if not already running
    // 🚀 PERFORMANCE: Removed logging, start immediately for static messages
    if (!animationRef.current && currentLengthRef.current < targetTextRef.current.length) {
      animationRef.current = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [text, speed, isStreaming, onComplete]); // 🚨 Removed displayedText from deps (causes re-render loop)

  // 🚀 PERFORMANCE: Use state to prevent cursor flicker (only updates when animation completes)
  const shouldShowCursor = displayedText.length < text.length && showCursor;
  
  // 🔧 FEB 4, 2026: Inject cursor into text content (not as separate element)
  // This keeps cursor inline with text, even after block-level paragraphs
  const displayContent = shouldShowCursor && !completedRef.current && displayedText.length < text.length
    ? `${displayedText}▊`  // Append cursor character to text
    : displayedText;
  
  return (
    <span className={className}>
      <Streamdown
        components={{
          // 🔧 FEB 4, 2026: Paragraph spacing between ideas
          // 🔧 FEB 9, 2026: Use CSS class so :last-child can remove trailing margin
          p: ({children}: any) => (
            <span className="cedar-paragraph">{children}</span>
          ),
          strong: ({children}: any) => <strong style={{fontWeight: 600}}>{children}</strong>,
          em: ({children}: any) => <em style={{fontStyle: 'italic'}}>{children}</em>,
          code: ({inline, children}: any) => 
            inline 
              ? <code style={{
                  backgroundColor: '#f3f4f6',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '0.9em',
                  fontFamily: 'Monaco, monospace'
                }}>{children}</code>
              : <pre><code>{children}</code></pre>,
          // 🎨 FEB 4, 2026: List formatting
          ul: ({children}: any) => <span className="cedar-list">{children}</span>,
          ol: ({children}: any) => <span className="cedar-list cedar-list-ordered">{children}</span>,
          li: ({children}: any) => <span style={{
            display: 'block',
            marginBottom: '0.35em',
            lineHeight: '1.5'
          }}>• {children}</span>,
          // 🔧 FEB 10, 2026: Handle action: protocol links from LLM output
          // Streamdown renders [Connect Garmin](action:connect_garmin) as <a href="action:connect_garmin">
          // Browsers block non-standard protocols, showing "[blocked]" instead
          // Solution: Hide action: links here - Chat.tsx renders proper action buttons via parseMessageActions
          a: ({href, children}: any) => {
            if (typeof href === 'string' && href.startsWith('action:')) {
              // Action links are handled by Chat.tsx action button system
              // Hide them from markdown rendering to prevent browser "[blocked]" display
              return null;
            }
            // Regular links render normally
            return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
          },
          // 🔧 FEB 4, 2026: Text component - style the cursor when it appears
          text: ({children}: any) => {
            // If text ends with ▊ (cursor), style it
            if (typeof children === 'string' && children.endsWith('▊')) {
              const textWithoutCursor = children.slice(0, -1);
              return (
                <>
                  {textWithoutCursor}
                  <span className="typing-cursor" style={{
                    color: '#1F6C4F',
                    fontWeight: 'bold',
                    animation: 'typingBlink 1s infinite'
                  }}>▊</span>
                </>
              );
            }
            return <>{children}</>;
          },
        }}
      >
        {displayContent}
      </Streamdown>
    </span>
  );
};

// 🚀 PERFORMANCE FIX: Memoize to prevent re-rendering ALL messages on every chunk
// Without memo, every SSE chunk causes ALL TypingText components to recreate (100+ per message)
export default React.memo(TypingText, (prevProps, nextProps) => {
  // Only re-render if text, isStreaming, or onComplete actually changed
  return (
    prevProps.text === nextProps.text &&
    prevProps.isStreaming === nextProps.isStreaming &&
    prevProps.speed === nextProps.speed &&
    prevProps.className === nextProps.className &&
    prevProps.onComplete === nextProps.onComplete
  );
}); 